import type { ChainInfo } from "@alch/dx-entities";
import type { GlobalProductLandingPagePath } from "@features/product-landing-global/lib/types";
import type {
  ProductLandingPagePath,
  ProductLandingPageTab,
} from "@features/product-landing/lib/types";
import { SearchParam } from "@hooks/useSearchParam.ts";

enum SettingsTab {
  AccessKeys = "access-keys",
  Security = "security",
  Profile = "profile",
  Team = "team",
}

export enum BillingPageTab {
  ManagePlan = "manage-plan",
  Invoices = "invoices",
  PaymentDetails = "payment-details",
}

enum Path {
  Home = "/",
  Apps = "/apps",
  CreateApp = `${Path.Apps}/new`,
  Services = "/services",
  Products = "/products",
  Logout = "/logout",
  Logs = "/logs",
  LogsRequests = `${Path.Logs}/requests`,
  LogsWalletApis = `${Path.Logs}/wallet-apis`,
  Sandbox = "/sandbox",
  Support = "/support",
  Mempool = "/mempool",
  Usage = "/usage",
  Webhooks = "/webhooks",
  WebhooksCreate = `${Path.Webhooks}/create`,
  AlertsHub = "/alerts",
  Chains = "/chains",
  Throughput = "/throughput",
  ActivityLogs = "/activity-log",
  ChainAnalytics = "/chain-analytics",

  // Chain Services
  Rollups = "/rollups",
  RollupsDeploy = "/rollups/deploy",

  // Wallet Services
  GasManager = "/gas-manager",
  GasManagerPump = `${Path.GasManager}/gas-pump`,
  GasManagerPolicy = `${Path.GasManager}/policy`,
  GasManagerPolicyCreate = `${Path.GasManagerPolicy}/create`,
  Accounts = "/accounts",
  AccountsConfigCreate = `${Path.Accounts}/config/create`,

  // Settings
  Settings = "/settings",
  Security = `${Path.Settings}/${SettingsTab.Security}`,
  Billing = `${Path.Settings}/billing`,
  BillingSpendingCap = `${Path.Billing}/${BillingPageTab.ManagePlan}#spending-cap`,
  PaymentDetails = `${Path.Billing}`,
  Downgrade = `${Path.Settings}/billing/downgrade`,
  Profile = `${Path.Settings}/${SettingsTab.Profile}`,
  Team = `${Path.Settings}/${SettingsTab.Team}`,
  ConfigureAlerts = `${Path.Settings}/alerts`,

  //Signup
  Signup = "/signup",
  SignupTeam = `${Path.Signup}/team`,
  SignupProject = `${Path.Signup}/project`,
}

export type AppDetailsTab =
  | "setup"
  | "wallets"
  | "metrics"
  | "networks"
  | "settings"
  | "security";

export enum NetworksTab {
  CoreRpc = "core-rpc",
  Dedicated = "dedicated",
}

export enum UsagePageTab {
  Overview = "overview",
  Http = "http",
  WebSockets = "websockets",
  Webhooks = "webhooks",
  YellowstoneGrpc = "yellowstone-grpc",
  AppBreakdown = "app-breakdown",
}

export const USAGE_PAGE_TABS = Object.values(UsagePageTab).join("|");

export enum LogsTab {
  Request = "requests",
  WalletApi = "wallet-apis",
}

export const LOGS_TABS = Object.values(LogsTab).join("|");

export enum RollupDetailPageTab {
  Overview = "overview",
  Config = "config",
}

const pathBuilder = {
  appDetails: (id: string, tab?: AppDetailsTab) =>
    `${Path.Apps}/${id}${tab == null ? "" : `/${tab}`}`,
  appNetworks: (id: string, tab?: NetworksTab) =>
    `${Path.Apps}/${id}/networks${tab == null ? "" : `/${tab}`}`,
  appSandbox: (id: string) => `${Path.Apps}/${id}/sandbox`,
  billing: (tab: `${BillingPageTab}`) => `${Path.Billing}/${tab}`,
  gasManager: {
    editPolicy: (id: string) => `${Path.GasManagerPolicy}/${id}/edit`,
    reviewPolicy: (id: string) => `${Path.GasManagerPolicy}/${id}`,
    grant: (id: string) => `${Path.GasManager}/grant/${id}`,
    approveGrant: (gasPumpId: string, policyId: string) =>
      `${Path.GasManagerPump}/${gasPumpId}/${policyId}/approve`,
    createPolicy: () => `${Path.GasManagerPolicy}/create`,
  },
  accounts: {
    editConfig: (id: string) => `${Path.Accounts}/config/${id}/edit`,
  },
  alerts: {
    editAlert: (id: number) => `${Path.ConfigureAlerts}/${id}`,
  },
  chainServices: {
    rollupDetails: (args: { raasId: string; tab: RollupDetailPageTab }) =>
      `${Path.Rollups}/${args.raasId}/${args.tab}`,
  },
  logs: {
    tab: (tab: `${LogsTab}`) => `${Path.Logs}/${tab}`,
    requests: () => Path.LogsRequests,
    walletApis: () => Path.LogsWalletApis,
    perApp: (appId: string) => `${Path.LogsRequests}/${appId}`,
  },
  mempool: {
    transaction: (txnHash: string) => `${Path.Mempool}/${txnHash}`,
  },
  services: {
    root: (path: ProductLandingPagePath) => `${Path.Services}/${path}`,
    landingPage: (
      path: ProductLandingPagePath,
      tab: ProductLandingPageTab = "overview",
    ) => `${Path.Services}/${path}/${tab}`,
  },
  products: {
    globalLandingPage: (path: GlobalProductLandingPagePath) =>
      `${Path.Products}/${path}`,
  },
  chains: {
    chainPage: (chainInfo: ChainInfo) =>
      `${Path.Chains}/${chainInfo.id.toLowerCase()}`,
  },
  chainAnalytics: {
    chainPage: (chainId: string) =>
      `${Path.ChainAnalytics}/${chainId.toLowerCase()}`,
  },
  webhooks: {
    createDetails: (initialData: {
      webhookType: number;
      networkId?: string;
      chainId?: string;
    }) => {
      const { webhookType, networkId, chainId } = initialData;

      const searchParams = new URLSearchParams();

      // Webhook type is required
      searchParams.append(
        SearchParam.InitialWebhookType,
        webhookType.toString(),
      );

      if (networkId) {
        searchParams.append(SearchParam.InitialWebhookNetwork, networkId);
      }
      if (chainId) {
        searchParams.append(SearchParam.InitialWebhookChain, chainId);
      }
      return `${Path.WebhooksCreate}?${searchParams.toString()}`;
    },
    details: (id: string) => `${Path.Webhooks}/${id}`,
  },
  usagePage: (tab: `${UsagePageTab}`) => `${Path.Usage}/${tab}`,
};

export { Path, pathBuilder, SettingsTab };
